/**
 * Employees store
 *
 * Holds a list of all employees and their permissions. It listens to Realtime events to keep the list up-to-date
 *
 */

import { computed, ref, reactive } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useApiStore } from '@/stores/Api'
import { usePhonebookStore } from '@/stores/Phonebook'

import Employee from './Employee'
import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import _i18n from '@/utilities/I18n'
import { PbxAppEmployeesExtensionCallOriginator, PbxAppGroupPermissionName, type PbxAppPermissionsResponse, type PbxAppEmployeesExtensionCallWithDate, type PbxAppEmployeesExtensionWithDate, type PbxAppEmployeesItem, type PbxAppGroupPermissionsResponse, type PbxAppEmployeesResponse } from '@/TcxApi/PbxApp'
import { type ConfigEventCommon, type PbxEventCall, type PbxEventEmployeePresenceChanged, type ConfigPbxExtensionCreated, type ConfigPbxExtensionUpdated,
	type ConfigEventPbxExtensionDeleted, PbxEventType, type ConfigEventPbxExtensionDndUpdated, type PbxEventPersonalQueueCleanup, type PbxEventPersonalQueueJoin, type PbxEventPersonalQueueLeave,
	type ConfigEventPbxCalendarEvent,
	type PbxEventCallVariable } from '@per_moeller/tcx-realtime-client'
import { dynamicSort, sleep } from '@/utilities/Tools'
import type { AxiosError } from 'axios'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import type { PbxCalendarResponse } from '@/TcxApi/PbxCalendar'

const DEFAULT_PERMISSIONS: PbxAppGroupPermissionsResponse = { pickup: false, steal: false, spy: false, setPresence: false, queueMember: false, viewOtherParty: false,
	readCalendar: false, writeCalendar: false, readPhonebook: false, writePhonebook: false }

export const useEmployeesStore = defineStore('employees', () => {
	logger.debug('Employees: Initializing store')

	const ss = useSettingsStore()
	const phonebook = usePhonebookStore()
	const i18n = _i18n.global
	const realtime = useRealtimeStore()
	const api = useApiStore()

	// #region Data

	const _ready = ref(false)
	const employees = ref<Employee[]>([])
	const permissions = ref<Record<string, PbxAppGroupPermissionsResponse>>({})
	const isReady = computed(() => _ready.value)
	const callVariables = reactive(new Map<string, string>())

	/** The user self */
	const me = computed(() => {
		return employees.value.find(employee => employee.me)
	})

	/**
	 * Returns the complete list of my onging calls
	 */
	const myCalls = computed(() => {
		const calls: PbxAppEmployeesExtensionCallWithDate[] = []

		for (const ext of me.value?.extensions ?? []) {
			for (const call of ext.calls) {
				calls.push(call)
			}
		}
		calls.sort(dynamicSort('started'))
		return calls
	})

	const myPhone = computed(() => {
		const employee = employees.value.find(employee => employee.me)

		if (!employee) { return null }

		const phone = employee.extensionSettings?.phones?.find(phone => phone?._id === ss.realPhoneId)

		if (phone && phone._id === ss.realPhoneId) {
			return phone
		}
		return null
	})

	// #endregion

	// #region Initializing

	/**
	 * Load all employees and their permissions
	 * @param midnightRefresh If true, this is a midnight refresh, if false this is the initial load
	 */
	async function init(midnightRefresh = false) {
		try {
			logger.info('Employees: Loading')
			_ready.value = false
			await ss.ready()
			await loadEmployees(midnightRefresh)
			await loadPermissions()
			oneMinuteRefresh() // Refresh all employees calendar events
			_ready.value = true
			globalEvents.emit('Employees:Loaded')
			logger.info(`Employees: Loaded ${employees.value.length} employees`)
		}
		catch (_) {
			// Dont care because realtime will ensure we see an error message if data cannot be fetched
		}
	}

	/** Startup event listeners */
	async function setupListeners() {
		realtime.configEvents.on('EMPLOYEE_CREATED', employeeCreatedEventHandler)
		realtime.configEvents.on('EMPLOYEE_UPDATED', employeeUpdatedEventHandler)
		realtime.configEvents.on('EMPLOYEE_DELETED', employeeDeletedEventHandler)
		realtime.configEvents.on('PBX_EXTENSION_CREATED', extensionCreatedEventHandler)
		realtime.configEvents.on('PBX_EXTENSION_UPDATED', extensionUpdatedEventHandler)
		realtime.configEvents.on('PBX_EXTENSION_DELETED', extensionDeletedEvent)
		realtime.configEvents.on('PBX_GROUP_CREATED', loadPermissions)
		realtime.configEvents.on('PBX_GROUP_UPDATED', loadPermissions)
		realtime.configEvents.on('PBX_GROUP_DELETED', loadPermissions)
		realtime.configEvents.on('PBX_EXTENSION_DND_UPDATED', extensionDndUpdatedEventHandler)
		realtime.configEvents.on('PBX_CALENDAR_CREATED', calendarEventHandler)
		realtime.configEvents.on('PBX_CALENDAR_UPDATED', calendarEventHandler)
		realtime.configEvents.on('PBX_CALENDAR_DELETED', calendarEventHandler)
		realtime.configEvents.on('PBX_CALENDAR_DELETE_ALL', calendarEventHandler)

		realtime.pbxEvents.on('CALL_START', callEventHandler)
		realtime.pbxEvents.on('CALL_RING', callEventHandler)
		realtime.pbxEvents.on('CALL_ANSWER', callEventHandler)
		realtime.pbxEvents.on('CALL_END', callEventHandler)
		realtime.pbxEvents.on('CALL_UPDATE', callEventHandler)
		realtime.pbxEvents.on('CALL_HOLD', callEventHandler)
		realtime.pbxEvents.on('CALL_UNHOLD', callEventHandler)
		realtime.pbxEvents.on('EMPLOYEE_PRESENCE_CHANGED', employeePresenceUpdatedEventHandler)
		realtime.pbxEvents.on('PERSONAL_QUEUE_CLEANUP', personalQueueCleanupEventHandler)
		realtime.pbxEvents.on('PERSONAL_QUEUE_JOIN', personalQueueJoinEventHandler)
		realtime.pbxEvents.on('PERSONAL_QUEUE_LEAVE', personalQueueLeaveEventHandler)
		realtime.pbxEvents.on('CALL_VARIABLE', callVariableEventHandler)

		realtime.realtimeEvents.on('Connected', init)

		globalEvents.on('Auth:LoggedOut', () => {
			employees.value = []
			permissions.value = {}
			_ready.value = false
		})

		globalEvents.on('Midnight', midnightRefresh)

		globalEvents.on('OneMinute', oneMinuteRefresh)

		globalEvents.on('StatusRequest', () => {
			globalEvents.emit('StatusReport', { key: 'employees', value: _ready.value })
		})

		// globalEvents.on('EmployeeReloadMyExtensions', async () => {
		// 	const employee = employees.value.find(employee => employee.me)

		// 	if (employee) {
		// 		await employee.loadExtensionSettings()
		// 	}
		// })
	}

	/** Invoked on midnight to reload all data */
	function midnightRefresh() {
		logger.info('Employees: Performing midnight refresh')
		init(true) // will force all employees shown to reload their data and thus update things like expiring texts
	}

	function oneMinuteRefresh() {
		for (const employee of employees.value) {
			employee.calculateCurrentOrNextCalendarEvent()
		}
	}

	/**
	 * Load employes from the API
	 * @param midnightRefresh If true, this is a midnight refresh, if false this is the initial load
	 */
	async function loadEmployees(midnightRefresh = false) {
		const res = await api.get<PbxAppEmployeesItem[]>('/pbx/app/employees?calls=true&standalone=true')

		if (midnightRefresh) {
			// Build set of new employee IDs for quick lookup
			const newEmployeeIds = new Set(res.data.map(emp => emp._id))

			// Remove employees that no longer exist (iterate backwards to safely splice)
			for (let idx = employees.value.length - 1; idx >= 0; idx--) {
				const emp = employees.value[idx]
				if (emp && !newEmployeeIds.has(emp._id)) {
					employees.value.splice(idx, 1)
				}
			}

			// Update existing employees or add new ones
			for (const rawEmployee of res.data) {
				const existingEmployee = employees.value.find(employee => employee._id == rawEmployee._id)
				if (existingEmployee) {
					existingEmployee.update(rawEmployee, permissions.value)
					// Reload extension settings for the current user to preserve myPhone reactivity
					if (existingEmployee.me && existingEmployee.primaryExtension) {
						await existingEmployee.loadExtensionSettings()
					}
				} else {
					const employee = new Employee(rawEmployee, permissions.value)

					if (employee.me && employee.primaryExtension) {
						await employee.loadExtensionSettings()
					}
					employees.value.push(reactive(employee))
				}
			}
		} else {
			employees.value = []

			for (const rawEmployee of res.data) {
				const employee = new Employee(rawEmployee, permissions.value)

				if (employee.me && employee.primaryExtension) {
					await employee.loadExtensionSettings()
				}
				employees.value.push(reactive(employee))
			}
		}
	}

	async function loadPermissions() {
		const res = await api.get<PbxAppPermissionsResponse>('/pbx/app/permissions')

		permissions.value = reactive(res.data)

		for (const employee of employees.value) {
			for (const ext of employee.extensions) {
				ext.permissions = permissions.value[ext._id] || DEFAULT_PERMISSIONS
			}
		}
	}

	// #endregion

	// #region Event handling

	/**
	 * Remove an employee/extension from the list
	 * @param id The ID of the employee/extension to remove
	 */
	function removeEmployee(id: string) {
		const index = employees.value.findIndex(employee => employee?._id === id)

		if (index !== -1) {
			employees.value.splice(index, 1)
		}
	}

	/**
	 * Handles when an employee is created
	 * @param event Event data
	 */
	async function employeeCreatedEventHandler(event: ConfigEventCommon) {
		try {
			await ready()
			const res = await api.get<PbxAppEmployeesItem[]>(`/pbx/app/employees?ids=${event.id}&calls=true`)

			if (!res.data || res.data.length !== 1) { return } // If not then employee/extension is not visible to Communicator apps

			const newEmployee = res.data[0] as PbxAppEmployeesItem
			const existingEmployee = employees.value.find(employee => employee?._id == newEmployee._id)

			if (existingEmployee) {
				existingEmployee.update(newEmployee, permissions.value)
			} else {
				employees.value.push(reactive(new Employee(res.data[0] as PbxAppEmployeesItem, permissions.value)))
			}
		}
		catch (err) {
			if ((err as AxiosError).status !== 0 && (err as AxiosError).code !== 'network') {
				globalEvents.emit('CommonErrorModal', { header: i18n.t('employees.errorHeader'), message: `${i18n.t('employees.errorLoadingNewEmployeeMessage')} ${(err as AxiosError).message}` })
			}
		}
	}

	/**
	 * Handles when an employee is updated
	 * @param event Event data
	 */
	async function employeeUpdatedEventHandler(event: ConfigEventCommon) {
		try {
			await ready()
			const res = await api.get<PbxAppEmployeesItem[]>(`/pbx/app/employees?ids=${event.id}&calls=true`)

			if (!res.data || res.data.length === 0) { // Employee/extension no longer visible to communicator apps
				removeEmployee(event.id)
				return
			}

			const existingEmployee = employees.value.find(employee => employee?._id == res.data[0]?._id)

			if (existingEmployee) {
				existingEmployee.update(res.data[0] as PbxAppEmployeesItem, permissions.value)
			} else {
				employees.value.push(reactive(new Employee(res.data[0] as PbxAppEmployeesItem, permissions.value)))
			}
		}
		catch (err) {
			if (event.id === ss.auth?.employee) { ss.logout(); return } // If we fail to load our self after an employee update - or we are no longer logged in - we are done

			if (ss.auth && (err as AxiosError).status !== 0 && (err as AxiosError).code !== 'network') {
				globalEvents.emit('CommonErrorModal', { header: i18n.t('employees.errorHeader'), message: `${i18n.t('employees.errorLoadingUpdatedEmployeeMessage')} ${(err as AxiosError).message}` })
			}
		}
	}

	/**
	 * Handles when an employee's presence is updated
	 * @param event Event data
	 */
	async function employeePresenceUpdatedEventHandler(event: PbxEventEmployeePresenceChanged) {
		await ready()
		const employee = employees.value.find(employee => employee?._id == event._id)

		if (employee) {
			employee.updatePresence(event.data)
		}
	}

	/**
	 * Handles when an employee is deleted
	 * @param event Event data
	 */
	async function employeeDeletedEventHandler(event: ConfigEventCommon) {
		await ready()
		removeEmployee(event.id)
	}

	/**
	 * Handles when an extension is created
	 * @param event Event data
	 */
	async function extensionCreatedEventHandler(event: ConfigPbxExtensionCreated) {
		try {
			await ready()
			const res = await api.get<PbxAppEmployeesResponse>(`/pbx/app/employees?ids=${event.data?.employeeId ?? event.id}&calls=true&standalone=true`)

			if (!res.data || res.data.length === 0) {
				return
			}

			const existingEmployee = employees.value.find(employee => employee?._id == res.data[0]?._id)
			if (existingEmployee) {
				existingEmployee.update(res.data[0] as PbxAppEmployeesItem, permissions.value)
				if (existingEmployee.me) { existingEmployee.loadExtensionSettings() }
			} else {
				const employee = new Employee(res.data[0] as PbxAppEmployeesItem, permissions.value)

				employees.value.push(reactive(employee))
				if (employee.me) { employee.loadExtensionSettings() }
			}
		}
		catch (err) {
			if (!ss.auth) { return }
			if (event.data.employeeId === ss.auth?.employee) { ss.logout(); return } // If we fail to load our self after an employee update - or we are no longer logged in - we are done

			if ((err as AxiosError).status !== 0 && (err as AxiosError).code !== 'network') {
				globalEvents.emit('CommonErrorModal', { header: i18n.t('employees.errorHeader'), message: `${i18n.t('employees.errorLoadingUpdatedEmployeeMessage')} ${(err as AxiosError).message}` })
			}
		}
	}

	/**
	 * Handles when an extension is updated
	 * @param event Event data
	 */
	async function extensionUpdatedEventHandler(event: ConfigPbxExtensionUpdated) {
		logger.debug('Employees: Extension updated event received for ' + (event.data?.employeeId ?? event.id))

		try {
			logger.debug('Employees: are we ready')
			await ready()
			logger.debug('Employees: Requsting updated employee data')
			const res = await api.get<PbxAppEmployeesResponse>(`/pbx/app/employees?ids=${event.data?.employeeId ?? event.id}&calls=true&standalone=true`)

			logger.debug('Employees: Data received - ' + res.data?.length + ' employees returned')

			if (!res.data || res.data.length === 0) {
				return
			}

			const employee = employees.value.find(employee => employee?._id == res.data[0]?._id)
			if (employee) {
				logger.debug('Employees: Updating existing employee')
				employee.update(res.data[0] as PbxAppEmployeesItem, permissions.value)
				if (employee.me) {
					logger.debug('Employees: Loading extension settings for me')
					employee.loadExtensionSettings()
				}
			} else {
				logger.debug('Employees: Adding new employee to list')
				const employee = new Employee(res.data[0] as PbxAppEmployeesItem, permissions.value)

				employees.value.push(reactive(employee))
				if (employee.me) {
					logger.debug('Employees: Loading extension settings for me (new employee)')
					employee.loadExtensionSettings()
				}
			}
		}
		catch (err) {
			if (!ss.auth) { return }
			if (event.data.employeeId === ss.auth?.employee) { ss.logout(); return } // If we fail to load our self after an employee update - or we are no longer logged in - we are done

			if ((err as AxiosError).status !== 0 && (err as AxiosError).code !== 'network') {
				globalEvents.emit('CommonErrorModal', { header: i18n.t('employees.errorHeader'), message: `${i18n.t('employees.errorLoadingUpdatedEmployeeMessage')} ${(err as AxiosError).message}` })
			}
		}
	}

	/**
	 * Hanldes when a phone on an extension changed its DND status
	 * @param event Event data
	 */
	async function extensionDndUpdatedEventHandler(event: ConfigEventPbxExtensionDndUpdated) {
		await ready()
		const employee = employees.value.find(employee => employee?._id == event.data.employeeId)

		if (employee?.me && employee.extensionSettings?._id == event.id && employee.extensionSettings.phones) {
			for (const phone of employee.extensionSettings.phones) {
				if (phone?._id === event.data.phoneId) {
					phone.dnd = event.data?.dnd
					break
				}
			}
		}
	}

	/**
	 * Handles when an extension is deleted
	 * @param event Event data
	 */
	async function extensionDeletedEvent(event: ConfigEventPbxExtensionDeleted) {
		await ready()
		const employee = employees.value.find(employee => (employee?._id == event.data?.employeeId ? event.data.employeeId : event.id))
		if (employee && employee._id === event.id) {
			// It is a standalone extension, so we will remove the entire thing
			removeEmployee(event.id)
		} else if (employee) {
			const index = employee.extensions.findIndex(ext => ext?._id == event.id)
			if (index !== -1) {
				employee.extensions.splice(index, 1)
				if (employee.me && employee.extensionSettings?._id == event.id) { employee.extensionSettings = undefined }
			}
		}
	}

	function callVariableEventHandler(event: PbxEventCallVariable) {
		if (!event.deleted) {
			callVariables.set(`${event.channel}-${event.name}`, event.value)
			logger.debug(`Employees: Call variable ${event.name} for channel ${event.channel} set to ${event.value}`)
		} else {
			callVariables.delete(`${event.channel}-${event.name}`)
			logger.debug(`Employees: Call variable ${event.name} for channel ${event.channel} deleted`)
		}
	}

	function callVariablesCleanup(call: PbxAppEmployeesExtensionCallWithDate) {
		for (const [key] of callVariables) {
			if (key.startsWith(`${call.channel}-`)) {
				callVariables.delete(key)
				logger.debug(`Employees: Call variable ${key} for channel ${call.channel} deleted (EOC)`)
			}
		}
	}

	/**
	 * Handles call events and updates employees/extensions as needed
	 * @param event Call event to handle
	 */
	async function callEventHandler(event: PbxEventCall) {
		await ready()
		const call = parseCall(event)

		if (call.state === PbxCallState.CALL_END) {
			callVariablesCleanup(call)
		}

		fixCallerCalleeNames(call)

		const employee = findEmployeeWithExtension(call.extension)

		if (employee) {
			const ext = employee.extensions.find(ext => ext?._id === call.extension)
			if (ext) {
				if (!ext.calls) { ext.calls = [] }

				switch (call.state) {
					case PbxCallState.CALL_END: {
						const extIndex = ext.calls.findIndex(extCall => extCall?._id === call._id)

						if (extIndex !== -1) {
							ext.calls.splice(extIndex, 1)
						}
						break
					}

					case PbxCallState.CALL_UPDATE: {
						const extCall = ext.calls.find(extCall => extCall?._id === call._id)

						if (extCall) {
							extCall.caller.number = call.caller.number
							extCall.caller.name = call.caller.name
							extCall.caller.secret = call.caller.secret
							extCall.callee.number = call.callee.number
							extCall.callee.name = call.callee.name
						}
						break
					}

					default: { // CALL_START, CALL_RING, CALL_ANSWER, CALL_UPDATE, CALL_HOLD, CALL_UNHOLD
						let iOwnHold: boolean | undefined = myCalls.value.every(aCall => aCall.callerChannelId !== call.callerChannelId)

						if (call.state !== PbxCallState.CALL_HOLD) { iOwnHold = undefined }

						const extCallIndex = ext.calls.findIndex(extCall => extCall?._id === call._id)

						if (extCallIndex !== -1) {
							const extCall = ext.calls[extCallIndex] as PbxAppEmployeesExtensionCallWithDate

							for (const _key of Object.keys(call)) {
								const key = _key as keyof PbxAppEmployeesExtensionCallWithDate

								(extCall as any)[key] = call[key] // eslint-disable-line @typescript-eslint/no-explicit-any
							}

							if (call.state === PbxCallState.CALL_HOLD) {
								extCall.iOwnHold = iOwnHold
							}
							if (call.state === PbxCallState.CALL_UNHOLD) {
								extCall.hold = undefined
								extCall.iOwnHold = undefined
								extCall.state = PbxCallState.CALL_ANSWER
							}
						} else {
							call.iOwnHold = iOwnHold
							ext.calls.push(call)
						}

						if (call.state === PbxCallState.CALL_UNHOLD) {
							const otherLegsCall = findOtherLegsCall(call)
							if (otherLegsCall) {
								otherLegsCall.hold = undefined
								otherLegsCall.iOwnHold = undefined
								otherLegsCall.state = PbxCallState.CALL_ANSWER
								otherLegsCall.holdOwner = false
							}
						}

						if (call.state === PbxCallState.CALL_HOLD) {
							const otherLegsCall = findOtherLegsCall(call)
							if (otherLegsCall) {
								otherLegsCall.hold = call.hold
								otherLegsCall.iOwnHold = iOwnHold
								otherLegsCall.state = call.state
								otherLegsCall.holdOwner = true
							}
						}
						break
					}
				}

				setExtensionState(ext)
			}
		} else {
			const iOwnHold = myCalls.value.some(aCall => aCall.callerChannelId !== call.callerChannelId)

			if (call.state === PbxCallState.CALL_UNHOLD) {
				const otherLegsCall = findOtherLegsCall(call)
				if (otherLegsCall) {
					otherLegsCall.hold = undefined
					otherLegsCall.iOwnHold = undefined
					otherLegsCall.state = PbxCallState.CALL_ANSWER
					otherLegsCall.holdOwner = false
				}
			}
			if (call.state === PbxCallState.CALL_HOLD) {
				const otherLegsCall = findOtherLegsCall(call)
				if (otherLegsCall) {
					otherLegsCall.hold = call.hold
					otherLegsCall.iOwnHold = iOwnHold
					otherLegsCall.state = call.state
					otherLegsCall.holdOwner = true
				}
			}
		}
	}

	/**
	 * Convert a call event to a extension call object, that can be added to an employees extension calls
	 * @param event Call event to parse
	 * @returns Extension call object
	 */
	function parseCall(event: PbxEventCall): PbxAppEmployeesExtensionCallWithDate {
		const id = event.eventId
		const data = event
		const call: PbxAppEmployeesExtensionCallWithDate = {
			_id: id,
			customer: data.customer,
			extension: data.extension as string,
			state: event.event as unknown as PbxCallState,
			caller: {
				number: data.caller,
				name: data.callerName,
				secret: data.callerPrivacy ?? false
			},
			callee: {
				number: data.callee,
				name: data.calleeName
			},
			sessionId: data.sessionId,
			channel: data.channel,
			callId: data.callId,
			direction: data.direction,
			leg: data.leg,
			originator: data.originator as unknown as PbxAppEmployeesExtensionCallOriginator,
			device: data.device,
			pbx: data.eventId.split(':')[0] as string,
			callerChannelId: data.callerChannelId,
			hideOtherParty: data.hideOtherParty,
			variables: {}
		}

		if (data.queueId) {
			call.caller.queueId = data.queueId
			call.caller.queueName = data.queueName
		}
		if (event.event === PbxEventType.CALL_START) { call.started = new Date() }
		if (event.event === PbxEventType.CALL_RING) { call.ringing = new Date() }
		if (event.event === PbxEventType.CALL_ANSWER) { call.answered = new Date() }
		if (event.event === PbxEventType.CALL_END) { call.ended = new Date() }
		if (event.event === PbxEventType.CALL_HOLD) { call.hold = new Date() }

		return call
	}

	/**
	 * Fix caller and callee names for a call, if possible
	 * - If the number is a star code, then replace it with the star code name
	 * - If the name is empty, then try to find the name of the employee that has the number
	 * @param call Call to fix the caller and callee names for
	 */
	function fixCallerCalleeNames(call: PbxAppEmployeesExtensionCallWithDate) {
		if (call.callee.number.startsWith('*') || call.callee.number.startsWith('#')) {
			const data = lookupStarCode(call.callee.number)

			if (data) {
				call.callee.number = data.number
				call.callee.name = data.name
			}
		}

		if (!call.caller.name) {
			// Rewrite star codes if possible
			if (call.caller.number.startsWith('*') || call.caller.number.startsWith('#') || call.caller.number === 'VOICEMAIL' || call.caller.number.startsWith('DND') ||
				call.caller.number.startsWith('SWITCH-')) {
				const data = lookupStarCode(call.caller.number)

				if (data) {
					call.caller.number = data.number
					call.caller.name = data.name
				}
			}

			// Lookup name in employees
			if (!call.caller.name) {
				const name = findNameFromNumber(call.caller.number)
				if (name) { call.caller.name = name }
			}

			// Lookup name in phonebook
			if (!call.caller.name) {
				const name = phonebook.findNameFromNumber(call.caller.number)
				if (name) { call.caller.name = name }
			}
		}

		if (!call.callee.name) {
			// Rewrite star codes if possible
			if (call.callee.number.startsWith('*') || call.callee.number.startsWith('#') || call.caller.number === 'VOICEMAIL' || call.caller.number.startsWith('DND') ||
				call.caller.number.startsWith('SWITCH-')) {
				const data = lookupStarCode(call.callee.number)

				if (data) {
					call.callee.number = data.number
					call.callee.name = data.name
				}
			}

			// Lookup name in employees
			if (!call.callee.name) {
				const name = findNameFromNumber(call.callee.number)
				if (name) { call.callee.name = name }
			}

			// Lookup name in phonebook
			if (!call.callee.name) {
				const name = phonebook.findNameFromNumber(call.callee.number)
				if (name) { call.callee.name = name }
			}
		}
	}

	/**
	 * Lookup the name and number to display for a star code
	 * @param number Phone number to lookup
	 * @returns Name and number to display for the given number or null if not found
	 */
	function lookupStarCode(number: string) {
		if (number === 'VOICEMAIL') { return { number: '', name: i18n.t('employees.voicemail') } }
		if (number === 'DND' || number.startsWith('DND')) { return { number: '', name: i18n.t('employees.dnd') } }
		if (number.startsWith('SWITCH-')) { return { number: '', name: i18n.t('employees.setSwitch') + ' ' + number.substring(7) } }
		if (number === '*') { return { number: '*', name: i18n.t('employees.mainMenu') } }
		if (number.startsWith('*1')) { return { number: number, name: i18n.t('employees.voicemail') } }
		if (number.startsWith('*2')) { return { number: number, name: i18n.t('employees.transfer') } }
		if (number.startsWith('*3')) { return { number: number, name: i18n.t('employees.greeting') } }
		if (number.startsWith('*4')) { return { number: number, name: i18n.t('employees.recordings') } }
		if (number === '*5') { return { number: number, name: i18n.t('employees.queueMenu') } }
		if (RegExp(/^\*5\*\d+$/).exec(number)) { return { number: number, name: i18n.t('employees.queueJoin') + ' ' + number.substring(3) } }
		if (RegExp(/^\*5\*\d+\*\d+$/).exec(number)) { return { number: number, name: i18n.t('employees.queueJoin') + ' ' + number.substring(3) } }
		if (RegExp(/^\*51\*\d+$/).exec(number)) { return { number: number, name: i18n.t('employees.queueIsMember') + ' ' + number.substring(4) } }
		if (number == '*53') { return { number: number, name: i18n.t('employees.queueMemberList') } }
		if (number == '#5' || number == '*55') { return { number: number, name: i18n.t('employees.queueLeaveAll') } }
		if (number.startsWith('#5#')) { return { number: number, name: i18n.t('employees.queueLeave') + ' ' + number.substring(3) } }
		if (number.startsWith('*6') || number.startsWith('#6')) { return { number: number, name: i18n.t('employees.blackWiteList') } }
		if (number === '*7') { return { number: number, name: i18n.t('employees.pickupMenu') } }
		if (number === '*71') { return { number: number, name: i18n.t('employees.pickupRinging') } }
		if (number.startsWith('*71*')) { return { number: number, name: i18n.t('employees.pickupRingingFrom') + ' ' + number.substring(4) } }
		if (number.startsWith('*72*')) { return { number: number, name: i18n.t('employees.stealCallFrom') + ' ' + number.substring(4) } }
		if (number.startsWith('*73*')) { return { number: number, name: i18n.t('employees.spyOn') + ' ' + number.substring(4) } }
		if (number.startsWith('*9') || number.startsWith('#9')) { return { number: number, name: i18n.t('employees.setPresence') } }
		if (number.startsWith('*0') || number.startsWith('#0')) { return { number: number, name: i18n.t('employees.hotdesk') } }
		if (number === '***') { return { number: number, name: i18n.t('employees.moveCall') } }
		return null
	}

	/**
	 * Lookup the name of the employee that has the given number, if any
	 * @param number Phone number to find the name for
	 * @returns Name of the employee that has the given number or empty string if not found
	 */
	function findNameFromNumber(number: string) {
		let name = ''
		const numberWithoutPrefix = ss.cutNationalPrefix(number)

		for (const emp of employees.value) {
			for (const ext of emp.extensions) {
				if (ext.number === numberWithoutPrefix || ext.direct === number || ext.mobile?.includes(number)) {
					name = emp.name
				}
			}
		}
		return name
	}

	/**
	 * Lookup the employee that has the given phone number
	 * @param number Phone number to find the employee for
	 * @returns Employee that has the given number or undefined if not found
	 */
	function findEmployeeFromNumber(number: string) {
		const numberWithoutPrefix = ss.cutNationalPrefix(number)
		if (!number.startsWith('+') && number.length >= 8) { number = ss.pbxSettings.nationalPrefix + number }
		return employees.value.find(employee => {
			return employee.extensions.some(ext => ext.number === numberWithoutPrefix || ext.direct === number || ext.mobile?.includes(number))
		})
	}

	/**
	 * Lookup the employee that has the extension with the given id
	 * @param id Id of the extension to find the employee for
	 * @returns Employee that has the extension with the given id or undefined if not found
	 */
	function findEmployeeWithExtension(id: string) {
		return employees.value.find(emp => {
			return emp.extensions.find(ext => ext?._id === id)
		})
	}

	/**
	 * Set the state for an extension based on its calls
	 * @param ext Extension to set the state for
	 */
	function setExtensionState(ext: PbxAppEmployeesExtensionWithDate) { // IDLE, CALLING, RINGING, BUSY
		if (ext.calls.length === 0) {
			ext.state = 'IDLE'
		} else if (ext.calls.length === 1) { // One ongoing call
			const call = ext.calls[0] as PbxAppEmployeesExtensionCallWithDate

			switch (call.state) {
				case PbxCallState.CALL_START: ext.state = call.leg === 'I' ? 'CALLING' : 'IDLE'; break // outbound call or inbound call (want for ringing before changing state)
				case PbxCallState.CALL_RING: ext.state = 'RINGING'; break // inbound call, ringing (because outbound leg goes START->ANSWER->END)
				case PbxCallState.CALL_ANSWER: ext.state = 'BUSY'; break
			}
		} else if (ext.calls.some(call => call.state === PbxCallState.CALL_ANSWER)) { // if any call is answered, then we are busy // // Multiple ongoing calls
			ext.state = 'BUSY'
		} else if (ext.calls.some(call => call.state === PbxCallState.CALL_RING)) { // else if any call is ringing - then we re ringing
			ext.state = 'RINGING'
		} else { // otherwise its to early and we are still shown as IDLE
			ext.state = 'IDLE'
		}
	}

	/**
	 * Find the other leg of a call, if possible
	 * @param call Call for which to find the other leg
	 * @returns The other leg of the call or undefined if not found
	 */
	function findOtherLegsCall(call: PbxAppEmployeesExtensionCallWithDate) {
		for (const employee of employees.value) {
			try {
				const potentialOtherCallLeg = employee.getCallfromSessionId(call.sessionId)
				if (potentialOtherCallLeg && potentialOtherCallLeg?._id !== call?._id) {
					return potentialOtherCallLeg
				}
			}
			catch (err) {
				logger.debug(`Employees: Error while looking for other leg of call ${call?._id} in employee ${employee?._id} ${employee.name}: ${(err as Error).message}`)
			}
		}
		return undefined
	}

	/**
	 * Handle a personal queue cleanup event
	 * @param event Event data
	 */
	async function personalQueueCleanupEventHandler(event: PbxEventPersonalQueueCleanup) {
		await ready()
		const employee = await getEmployeeByExtensionId(event._id)
		const ext = employee?.extensions.find(ext => ext._id === event._id)

		if (ext) {
			ext.personalQueue = event.calls
			logger.debug(`Employees: Personal queue cleanup for ${ext._id} ${ext.number} - now has ${event.calls} calls`)
		}
	}

	/**
	 * Handle a personal queue join event
	 * @param event Event data
	 */
	async function personalQueueJoinEventHandler(event: PbxEventPersonalQueueJoin) {
		await ready()
		const employee = await getEmployeeByExtensionId(event._id)
		const ext = employee?.extensions.find(ext => ext._id === event._id)

		if (ext) {
			ext.personalQueue = event.calls
			logger.debug(`Employees: Personal queue join for ${ext._id} ${ext.number} - now has ${event.calls} calls`)
		}
	}

	/**
	 * Handle a personal queue leave event
	 * @param event Event data
	 */
	async function personalQueueLeaveEventHandler(event: PbxEventPersonalQueueLeave) {
		await ready()
		const employee = await getEmployeeByExtensionId(event._id)
		const ext = employee?.extensions.find(ext => ext._id === event._id)

		if (ext) {
			ext.personalQueue = event.calls
			logger.debug(`Employees: Personal queue leave for ${ext._id} ${ext.number} - now has ${event.calls} calls`)
		}
	}

	/**
	 * Hanldes when a phone on an extension changed its DND status
	 * @param event Event data
	 */
	async function calendarEventHandler(event: ConfigEventPbxCalendarEvent) {
		await ready()
		const employee = employees.value.find(employee => employee?._id == event.data.employee)
		if (!employee) { return } // Not for a known employee = we do not care

		switch (event.event) {
			case 'PBX_CALENDAR_CREATED': {
				try {
					const res = await api.get<PbxCalendarResponse>(`/pbx/calendar/${event.id}`)

					res.data.start = new Date(res.data.start)
					res.data.end = new Date(res.data.end)
					employee.calendar.push(res.data)
					employee.calculateCurrentOrNextCalendarEvent()
				}
				catch (_) { /** Ignore */ }
				break
			}

			case 'PBX_CALENDAR_UPDATED': {
				try {
					const res = await api.get<PbxCalendarResponse>(`/pbx/calendar/${event.id}`)

					res.data.start = new Date(res.data.start)
					res.data.end = new Date(res.data.end)

					const index = employee.calendar.findIndex(cal => cal._id === event.id)
					if (index !== -1) {
						employee.calendar[index] = res.data
					} else {
						employee.calendar.push(res.data)
					}
					employee.calculateCurrentOrNextCalendarEvent()
				}
				catch (_) { /** Ignore */ }
				break
			}

			case 'PBX_CALENDAR_DELETED': {
				const index = employee.calendar.findIndex(cal => cal._id === event.id)
				if (index !== -1) {
					employee.calendar.splice(index, 1)
				}
				employee.calculateCurrentOrNextCalendarEvent()
				break
			}

			case 'PBX_CALENDAR_DELETE_ALL': {
				employee.calendar = []
				employee.calculateCurrentOrNextCalendarEvent()
				break
			}
		}
	}

	// #endregion

	// #region Public function

	/** Returns when employees are loaded and we are ready to serve data */
	async function ready() {
		if (_ready.value) { return }

		while (!_ready.value) {
			await sleep(250)
		}
	}

	/**
	 * Get a single employee/extension
	 * @param id Id of employee/extension to lookup
	 */
	function getEmployee(id: string) : Employee | undefined {
		return employees.value.find(employee => employee?._id === id)
	}

	/**
	 * Get a single employee who has a given extension number
	 * @param extensionNumber Extension number to lookup
	 */
	async function getEmployeeByExtensionNumber(extensionNumber: string) : Promise<Employee | undefined> {
		await ready()
		return employees.value.find(employee => {
			return employee.extensions.some(extension => extension.number === extensionNumber)
		})
	}

	/**
	 * Get a single employee who has a given extension id
	 * @param extension Extension id
	 */
	async function getEmployeeByExtensionId(extension: string) : Promise<Employee | undefined> {
		await ready()
		return employees.value.find(employee => {
			return employee.extensions.some(ext => ext?._id === extension)
		})
	}

	/**
	 * Do you have permission to perform a given action on the specified employee
	 * @param employeeId Id of employee to perform action on
	 * @param type Type of action: pickup, steal, spy, setPresence, queueMember, viewOtherParty, readCalendar, writeCalendar, readPhonebook, writePhonebook
	 */
	async function hasPermission(employeeId: string, type: PbxAppGroupPermissionName) {
		await ready()
		if (permissions.value[employeeId]) { return permissions.value[employeeId][type] || false }
		return false
	}

	/**
	 * Returns the complete list of my ongoing calls (promise version)
	 */
	async function getMyCalls() {
		await ready()
		return myCalls.value
	}

	// #endregion

	// #region Initialization

	setupListeners()

	if (realtime.connected) { // otherwise it is invoked on Realtime:Connected
		init()
	}

	return { ready, isReady, employees, permissions, me, getEmployee, getEmployeeByExtensionNumber, getEmployeeByExtensionId, hasPermission, getMyCalls, myCalls, myPhone, callVariables,
		findEmployeeFromNumber, findEmployeeWithExtension, findNameFromNumber, setExtensionState, fixCallerCalleeNames, lookupStarCode, findOtherLegsCall }

	// #endregion
})
